import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import * as MsalBrowser from '@azure/msal-browser';
import { GlobalConfig } from './types';
import { appInsights } from 'services/applicationInsightsService';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

const localConfig: GlobalConfig = {
  vantageWebApi: process.env.REACT_APP_VANTAGE_WEB_API,
  msalSubscriptionKey: process.env.REACT_APP_MSAL_SUBSCRIPTION_KEY,
  xFunctionsKey: process.env.REACT_APP_X_FUNCTIONS_KEY,
  signalRSubscriptionKey: process.env.REACT_APP_SIGNALR_SUBSCRIPTION_KEY,
  signalRUrl: process.env.REACT_APP_SIGNALR_URL,
  b2cApplicationId: process.env.REACT_APP_MSAL_APPID,
  b2cAuthority: process.env.REACT_APP_MSAL_AUTHORITY,
  b2cRedirectUri: process.env.REACT_APP_MSAL_REDIRECTURI,
  b2cAuthorityDomain: process.env.REACT_APP_MSAL_AUTHORITYDOMAIN,
  b2cUserFlow: process.env.REACT_APP_MSAL_USERFLOW,
  b2cGroup: process.env.REACT_APP_MSAL_GROUP,
};

/**
 * Context for global configuration, which can include both default settings
 * and dynamically set environment-specific values.
 */
export const Config: React.Context<GlobalConfig | null> = createContext(null);

const config =
  process.env.REACT_APP_LOCAL === 'true' ? localConfig : (window as any).ReactAppConfig;

const ua = window.navigator.userAgent;
const msie = ua.indexOf('MSIE ');
const msie11 = ua.indexOf('Trident/');
const msedge = ua.indexOf('Edge/');
const firefox = ua.indexOf('Firefox');
const isIE = msie > 0 || msie11 > 0;
const isEdge = msedge > 0;
const isFirefox = firefox > 0;

export const b2cPolicies = {
  names: {
    signUpSignIn: config.b2cUserFlow,
  },
  authorities: {
    signUpSignIn: {
      authority: `${config.b2cAuthority}/${config.b2cAuthority}`,
    },
  },
  authorityDomain: config.b2cAuthority,
};

export const MsalConfig = {
  auth: {
    clientId: config.b2cApplicationId,
    authority: `${config.b2cAuthority}/${config.b2cUserFlow}`,
    knownAuthorities: [config.b2cAuthorityDomain],
    redirectUri: config.b2cRedirectUri,
    postLogoutRedirectUri: config.b2cRedirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: isIE || isEdge || isFirefox,
  },
  system: {
    allowNativeBroker: false, // Disables WAM Broker
    iframeHashTimeout: 10000,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case MsalBrowser.LogLevel.Error:
            appInsights.trackException(
              {
                exception: message,
                severityLevel: SeverityLevel.Error,
              },
              {
                componentStack: {
                  module: 'B2C Msal',
                  ...containsPii,
                },
              }
            );
            console.error(message);
            return;
          case MsalBrowser.LogLevel.Warning:
            appInsights.trackTrace(
              {
                message: message,
                severityLevel: SeverityLevel.Warning,
              },
              {
                componentStack: {
                  module: 'B2C Msal',
                  ...containsPii,
                },
              }
            );
            console.warn(message);
            return;
          // Use for debugging or get more information
          case MsalBrowser.LogLevel.Info:
            appInsights.trackTrace(
              {
                message: message,
                severityLevel: SeverityLevel.Information,
              },
              {
                componentStack: {
                  module: 'B2C Msal',
                  ...containsPii,
                },
              }
            );
            console.info(message);
            return;
          case MsalBrowser.LogLevel.Verbose:
            console.debug(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

export const loginRequest = {
  scopes: ['openid', 'offline_access', config.b2cApplicationId],
};

/**
 * Provides a React context provider for global configuration.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components that will have access to the context.
 * @returns {JSX.Element} The provider component that wraps children and provides them access to the global config.
 */
export const ConfigProvider = ({ children }) => {
  return (
    <Config.Provider
      value={
        /**
         * If the local env is not set we use
         * the options set on the window
         */
        {
          ...config,
        }
      }
    >
      {children}
    </Config.Provider>
  );
};

ConfigProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * Custom React hook to access the global configuration context.
 * @returns {GlobalConfig & {refreshTokenId: string; accessTokenId: string} | null} The current global configuration context.
 */
export const useConfig = () => useContext(Config);
